import {
  commaSeparatedStringsForQueryParam,
  parseCommaSeparatedStrings,
} from 'client-portal/utils/clinicianSearch/comma-query-param';
import { preselectionIdsForQueryParam } from './booking-query-params';

function officeIdsForRateIds(catalog, clinicianId, rateIds) {
  let officeIds = new Set();
  for (let rateId of rateIds) {
    for (let officeId of catalog.officesForCptCodeRateAndClinicianId(rateId, clinicianId) ?? []) {
      officeIds.add(String(officeId));
    }
  }
  return officeIds;
}

function rateIdsForClinicianAtOffice(catalog, clinicianId, officeId) {
  return catalog.cptCodeRateIdsForClinician(clinicianId, { officeIds: [officeId] });
}

function intersectIds(ids, allowSet) {
  return ids.filter(id => allowSet.has(id));
}

function narrowRatesToClinician(catalog, clinicianId, queryParams) {
  if (!clinicianId || !queryParams?.preselected) {
    return null;
  }

  let clinicianRateIds = new Set(catalog.cptCodeRateIdsForClinician(clinicianId));
  if (!clinicianRateIds.size) {
    return null;
  }

  let selectedRates = preselectionIdsForQueryParam(queryParams, 'preselectedCptCodeIds');
  let narrowedRates = selectedRates.filter(id => clinicianRateIds.has(id));
  return narrowedRates.length ? narrowedRates : null;
}

function narrowedOfficesForRates(ctx, narrowedRates) {
  let { catalog, clinicianId, queryParams } = ctx;
  let eligibleOfficeIds = officeIdsForRateIds(catalog, clinicianId, narrowedRates);
  let selectedOffices = preselectionIdsForQueryParam(queryParams, 'preselectedOfficeIds');

  return selectedOffices.length
    ? intersectIds(selectedOffices, eligibleOfficeIds)
    : [...eligibleOfficeIds];
}

function applySingleOfficeRateFilter(ctx, narrowedRates, narrowedOffices) {
  if (narrowedOffices.length !== 1) {
    return narrowedRates;
  }

  let { catalog, clinicianId } = ctx;
  let officeRates = new Set(rateIdsForClinicianAtOffice(catalog, clinicianId, narrowedOffices[0]));
  let officeNarrowed = narrowedRates.filter(id => officeRates.has(id));
  return officeNarrowed.length ? officeNarrowed : narrowedRates;
}

function buildNarrowedQueryParams({ catalog, clinicianId, queryParams } = {}) {
  let ctx = { catalog, clinicianId, queryParams };
  let narrowedRates = narrowRatesToClinician(catalog, clinicianId, queryParams);
  if (!narrowedRates) {
    return null;
  }

  let narrowedOffices = narrowedOfficesForRates(ctx, narrowedRates);
  narrowedRates = applySingleOfficeRateFilter(ctx, narrowedRates, narrowedOffices);

  return {
    ...queryParams,
    preselectedCptCodeIds: commaSeparatedStringsForQueryParam(narrowedRates) || null,
    preselectedOfficeIds: commaSeparatedStringsForQueryParam(narrowedOffices) || null,
  };
}

export function narrowQueryParamsForClinician({ catalog, clinicianId, queryParams } = {}) {
  return buildNarrowedQueryParams({ catalog, clinicianId, queryParams }) ?? queryParams;
}

export function canonicalCptCodeRecordForOffice({
  catalog,
  store,
  clinicianId,
  officeId,
  preselectedCptCodeIds,
} = {}) {
  let allow = new Set(parseCommaSeparatedStrings(preselectedCptCodeIds));
  if (!clinicianId || !officeId || !allow.size) {
    return null;
  }

  let rateIds = rateIdsForClinicianAtOffice(catalog, clinicianId, officeId);
  // Any matching rate is valid — the office already narrows the clinician's offering.
  let matchId = rateIds.find(id => allow.has(id));
  return matchId ? store.peekRecord('cpt-code', matchId) : null;
}

export function officeIdsForPreselection({
  catalog,
  clinicianId,
  queryParams = {},
  reservationCptCodeId,
} = {}) {
  let preselectedRateIds = preselectionIdsForQueryParam(queryParams, 'preselectedCptCodeIds');
  let preselectedOfficeIds = preselectionIdsForQueryParam(queryParams, 'preselectedOfficeIds');
  let rateIds = [...preselectedRateIds, reservationCptCodeId].filter(Boolean);

  let officeIds = officeIdsForRateIds(catalog, clinicianId, rateIds);

  if (preselectedOfficeIds.length) {
    officeIds = new Set(intersectIds([...officeIds], new Set(preselectedOfficeIds)));
  }

  return officeIds;
}
